import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { H3 } from '@/components/UI/Typography/Heading';
import { Button } from '@/components/UI/Button';
import { Delete } from '@/components/Icon';
import { convertTiming } from '@/utils/convert';
import { theme } from '@/theme/Theme';
import type { NotificationCenterItem } from '@/types/notifications';
import {
  CardWrapper,
  Description,
  NotificationActions,
  NotificationBody,
  NotificationCheckbox,
  NotificationRow,
  TimeDifference,
  UnreadDot,
} from './style';

type NotificationProps = {
  data: NotificationCenterItem[];
  selectedIds: number[];
  showSelect: boolean;
  archived?: boolean;
  onSelectionChange: (ids: number[]) => void;
  onArchive: (ids: number[]) => Promise<void>;
};

const NotificationCard = ({ data, showSelect, selectedIds, archived = false, onSelectionChange, onArchive }: NotificationProps) => {
  // @ts-ignore -- repository translation inference exceeds TypeScript's instantiation depth.
  const { t } = useTranslation();
  const navigate = useNavigate();

  const toggle = (id: number) => onSelectionChange(
    selectedIds.includes(id) ? selectedIds.filter((item) => item !== id) : [...selectedIds, id]
  );

  return <CardWrapper>
    {data.map((item) => <NotificationRow
      key={`notification-${item.id}`}
      $showSelect={showSelect}
      aria-labelledby={`notification-title-${item.id}`}
      aria-describedby={`notification-summary-${item.id}`}
      data-notification-id={item.id}
    >
      {showSelect && <NotificationCheckbox
        onChange={() => toggle(item.id)}
        checked={selectedIds.includes(item.id)}
        inputProps={{
          'aria-label': t('pages.home.notifications.selectNotification', { title: item.title }),
        }}
      />}
      <NotificationBody>
        <H3 id={`notification-title-${item.id}`} fontWeight="bold" marginBottom="8px">{item.title}</H3>
        <Description id={`notification-summary-${item.id}`}>{item.summary}</Description>
        <TimeDifference>{convertTiming(item.occurredAt)}</TimeDifference>
      </NotificationBody>
      <NotificationActions>
        <Button type="button" onClick={() => navigate(`/home/notifications/${item.id}`)} variant="regular" size="sm"
          aria-label={t('pages.home.notifications.showNotificationDetails', { title: item.title })}>
          {t('buttons.showDetails')}
        </Button>
        <Delete.FormDelete type="button" onClick={() => onArchive([item.id])}
          aria-label={archived
            ? t('pages.home.notifications.restoreNotification', { title: item.title })
            : t('pages.home.notifications.archiveNotification', { title: item.title })}>
          <Delete.DeleteSVG fill={theme.colors.gray400} aria-hidden="true" focusable="false" />
        </Delete.FormDelete>
      </NotificationActions>
      {!item.readAt && <UnreadDot aria-hidden="true" />}
    </NotificationRow>)}
  </CardWrapper>;
};

export default NotificationCard;
